import { useEffect, useState } from "react";
import { api, asArray } from "@/lib/api";
import { toast } from "sonner";
import { Package, Plus, Trash2, Rocket, X, Save, Server, Building2 } from "lucide-react";

// Software package catalogue: stored installers the agent fetches and installs
// silently (winget id, or a vendor MSI/EXE URL + switches). Deploy to one device
// or a whole client.

const inputCls = "w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200 focus:border-zinc-600 outline-none";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-zinc-950 border border-zinc-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-zinc-900">
          <div className="text-sm font-medium text-zinc-100">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function Software() {
  const [pkgs, setPkgs] = useState([]);
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deploy, setDeploy] = useState(null);   // package to deploy
  const [target, setTarget] = useState({ scope: "device", agent_id: "", client_id: "" });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", publisher: "", category: "Other", method: "winget", winget_id: "", url: "", kind: "msi", silent_args: "/qn /norestart", detect: "" });

  const load = () => Promise.all([
    api.get("/packages").then(r => setPkgs(asArray(r.data))),
    api.get("/agents").then(r => setAgents(asArray(r.data))),
    api.get("/clients").then(r => setClients(asArray(r.data))),
  ]).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const doDeploy = async () => {
    try {
      if (target.scope === "device") {
        if (!target.agent_id) { toast.error("Pick a device"); return; }
        const { data } = await api.post(`/agent/${target.agent_id}/deploy-package`, { package_id: deploy.id });
        toast.success(`${deploy.name} → deploy ${data.delivered ? "sent" : "queued"}`);
      } else {
        if (!target.client_id) { toast.error("Pick a client"); return; }
        const { data } = await api.post(`/clients/${target.client_id}/deploy-package`, { package_id: deploy.id });
        toast.success(`${deploy.name} → queued to ${data.queued_to} device(s)`);
      }
      setDeploy(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Deploy failed"); }
  };

  const addPackage = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (form.method === "winget" && !form.winget_id.trim()) { toast.error("winget id required"); return; }
    if (form.method === "url" && !form.url.trim()) { toast.error("URL required"); return; }
    try {
      await api.post("/packages", { ...form, detect: form.detect || form.name });
      toast.success("Package added"); setAdding(false);
      setForm({ name: "", publisher: "", category: "Other", method: "winget", winget_id: "", url: "", kind: "msi", silent_args: "/qn /norestart", detect: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the catalogue?`)) return;
    try { await api.delete(`/packages/${p.id}`); toast.success("Removed"); await load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1200px] mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center"><Package className="text-blue-400" size={18} /></div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-50">Software</h1>
            <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">Package catalogue · silent deployment</div>
          </div>
        </div>
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-blue-500/15 hover:bg-blue-500/25 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm"><Plus size={16} /> Add package</button>
      </div>

      {loading ? <div className="text-zinc-500 text-sm">Loading…</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {pkgs.map(p => (
            <div key={p.id} className="bg-zinc-950 border border-zinc-900 rounded-md p-4 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-zinc-100 truncate">{p.name}</div>
                  <div className="text-xs text-zinc-500">{p.publisher || "—"} · {p.category}</div>
                </div>
                <span className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded ${p.method === "winget" ? "bg-emerald-500/10 text-emerald-400" : "bg-blue-500/10 text-blue-400"}`}>{p.method}</span>
              </div>
              <div className="text-[11px] text-zinc-600 font-mono mt-2 truncate">{p.method === "winget" ? p.winget_id : p.url}</div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-900">
                <button onClick={() => { setDeploy(p); setTarget({ scope: "device", agent_id: "", client_id: "" }); }} className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded px-3 py-1.5 text-xs"><Rocket size={12} /> Deploy</button>
                {!p.builtin && <button onClick={() => remove(p)} className="ml-auto text-red-400/80 hover:text-red-300"><Trash2 size={13} /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* deploy modal */}
      {deploy && (
        <Modal title={`Deploy ${deploy.name}`} onClose={() => setDeploy(null)}>
          <div className="flex gap-2 mb-3">
            <button onClick={() => setTarget(t => ({ ...t, scope: "device" }))} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded px-3 py-2 text-sm border ${target.scope === "device" ? "bg-blue-500/15 border-blue-500/40 text-blue-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}><Server size={14} /> One device</button>
            <button onClick={() => setTarget(t => ({ ...t, scope: "client" }))} className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded px-3 py-2 text-sm border ${target.scope === "client" ? "bg-blue-500/15 border-blue-500/40 text-blue-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}><Building2 size={14} /> Whole client</button>
          </div>
          {target.scope === "device" ? (
            <select className={inputCls} value={target.agent_id} onChange={e => setTarget(t => ({ ...t, agent_id: e.target.value }))}>
              <option value="">Select a device…</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.hostname} {a.status === "online" ? "" : "(offline)"}</option>)}
            </select>
          ) : (
            <select className={inputCls} value={target.client_id} onChange={e => setTarget(t => ({ ...t, client_id: e.target.value }))}>
              <option value="">Select a client…</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <div className="text-[11px] text-zinc-600 mt-2">Installs silently via {deploy.method === "winget" ? `winget (${deploy.winget_id})` : "a downloaded installer"}. Offline devices install on next connect. Requires the agent running elevated (SYSTEM service).</div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setDeploy(null)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
            <button onClick={doDeploy} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm"><Rocket size={15} /> Deploy</button>
          </div>
        </Modal>
      )}

      {/* add package modal */}
      {adding && (
        <Modal title="Add package" onClose={() => setAdding(false)}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Name</div><input className={inputCls} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
            <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Publisher</div><input className={inputCls} value={form.publisher} onChange={e => setForm(f => ({ ...f, publisher: e.target.value }))} /></div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Method</div>
          <div className="flex gap-2 mb-3">
            {[["winget", "winget id"], ["url", "MSI / EXE URL"]].map(([k, l]) => (
              <button key={k} onClick={() => setForm(f => ({ ...f, method: k }))} className={`flex-1 rounded px-3 py-2 text-sm border ${form.method === k ? "bg-blue-500/15 border-blue-500/40 text-blue-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>{l}</button>
            ))}
          </div>
          {form.method === "winget" ? (
            <div className="mb-3"><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">winget id</div><input className={inputCls} placeholder="e.g. Google.Chrome" value={form.winget_id} onChange={e => setForm(f => ({ ...f, winget_id: e.target.value }))} /></div>
          ) : (
            <>
              <div className="mb-3"><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Installer URL</div><input className={inputCls} placeholder="https://…/installer.msi" value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Kind</div>
                  <select className={inputCls} value={form.kind} onChange={e => setForm(f => ({ ...f, kind: e.target.value }))}><option>msi</option><option>exe</option></select></div>
                <div><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Silent args</div><input className={inputCls} value={form.silent_args} onChange={e => setForm(f => ({ ...f, silent_args: e.target.value }))} /></div>
              </div>
            </>
          )}
          <div className="mb-4"><div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono mb-1.5">Detect (product name to confirm install)</div><input className={inputCls} placeholder={form.name} value={form.detect} onChange={e => setForm(f => ({ ...f, detect: e.target.value }))} /></div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="px-3.5 py-2 text-sm text-zinc-400 hover:text-zinc-200">Cancel</button>
            <button onClick={addPackage} className="inline-flex items-center gap-2 bg-blue-500/15 border border-blue-500/40 text-blue-200 rounded-md px-4 py-2 text-sm"><Save size={15} /> Add</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
